// components/HealthSensors/index.tsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SectionHeader from "../../SectionHeader";

// Import widgets
import SleepWidget from "./widgets/SleepWidget";
import HeartWidget from "./widgets/HeartWidget";
import SpO2Widget from "./widgets/SpO2Widget";
import ECGWidget from "./widgets/ECGWidget";
import HRVWidget from "./widgets/HRVWidget";
import StressWidget from "./widgets/StressWidget";
import StepsWidget from "./widgets/StepsWidget";
import TempWidget from "./widgets/TempWidget";
import SportsWidget from "./widgets/SportsWidget";
import GlucoseWidget from "./widgets/GlucoseWidget";
import WomenWidget from "./widgets/WomenWidget";
import FallWidget from "./widgets/FallWidget";
import BiologicalAgeWidget from "./widgets/BiologicalAgeWidget";
import MoodWidget from "./widgets/MoodWidget";
import ReadinessWidget from "./widgets/ReadinessWidget";
import LabAnalysisWidget from "./widgets/LabAnalysisWidget";
import NutritionAnalysisWidget from "./widgets/NutritionAnalysisWidget";

// Import components
import SensorTabs from "./SensorTabs";
import SensorContent from "./SensorContent";

// Import icons
import { Moon, Heart, Activity, Footprints, Thermometer, Dumbbell, Droplet, AlertTriangle, User, Smile, Zap, FileText, Utensils, Shield, Brain, Users, Baby, Briefcase } from "lucide-react";
import { GiLungs } from "react-icons/gi";
import { SlUserFemale } from "react-icons/sl";

const HealthSensors: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [activeTab, setActiveTab] = useState("sleep");
    const [isHovering, setIsHovering] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ FIX: تبدیل tabs از object به array
    const tabsData = t("tabs", { returnObjects: true });
    const tabs = Array.isArray(tabsData) ? tabsData : [];

    // Icon mapping
    const iconMap: Record<string, React.ReactNode> = {
        sleep: <Moon />,
        spo2: <GiLungs />,
        heart: <Heart />,
        ecg: <Activity />,
        hrv: <Activity />,
        women: <SlUserFemale />,
        stress: <Brain />,
        steps: <Footprints />,
        temp: <Thermometer />,
        sports: <Dumbbell />,
        glucose: <Droplet />,
        fall: <AlertTriangle />,
        bioAge: <User />,
        mood: <Smile />,
        readiness: <Zap />,
        labAnalysis: <FileText />,
        nutritionAnalysis: <Utensils />,
    };

    // Widget mapping
    const widgetMap: Record<string, React.ReactNode> = {
        sleep: <SleepWidget />,
        spo2: <SpO2Widget />,
        heart: <HeartWidget />,
        ecg: <ECGWidget />,
        hrv: <HRVWidget />,
        women: <WomenWidget />,
        stress: <StressWidget />,
        steps: <StepsWidget />,
        temp: <TempWidget />,
        sports: <SportsWidget />,
        glucose: <GlucoseWidget />,
        fall: <FallWidget />,
        bioAge: <BiologicalAgeWidget />,
        mood: <MoodWidget />,
        readiness: <ReadinessWidget />,
        labAnalysis: <LabAnalysisWidget />,
        nutritionAnalysis: <NutritionAnalysisWidget />,
    };

    // Scenario Icon mapping
    const scenarioIconMap: Record<string, React.ReactNode> = {
        woman: <SlUserFemale />,
        baby: <Baby />,
        dumbbell: <Dumbbell />,
        user: <User />,
        zap: <Zap />,
        brain: <Brain />,
        users: <Users />,
        heart: <Heart />,
        alert: <AlertTriangle />,
        briefcase: <Briefcase />,
        shield: <Shield />,
        smile: <Smile />,
    };

    const getActiveTabData = () => {
        if (!Array.isArray(tabs) || tabs.length === 0) {
            return {
                id: "sleep",
                title: "Sleep",
                color: "from-indigo-400 to-blue-500",
                description: "",
                scenarios: [],
            };
        }
        return tabs.find((tab: any) => tab.id === activeTab) || tabs[0];
    };

    const activeTabData = getActiveTabData();

    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.1,
            },
        },
    };

    if (!Array.isArray(tabs) || tabs.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-gray-600">Loading health sensors...</p>
            </div>
        );
    }

    return (
        <section dir={isRTL ? "rtl" : "ltr"} className="relative py-10 md:py-16 rounded-2xl px-4 md:px-8 mb-8 md:mb-16 overflow-hidden">
            {/* Background with Light Gradient and Glassy Blur */}

            {/* Glassmorphism Overlay */}
            <div className="absolute inset-0  rounded-3xl" />

           
            <div className="relative max-w-7xl mx-auto">
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col-reverse md:flex-row items-center justify-between">
                    {/* Content Section */}
                    <div className="w-full">
                        {/* Header */}
                        <SectionHeader
                            regularText={t("header.regularText")}
                            highlightedText={t("header.highlightedText")}
                            descriptionHighlight={t("header.descriptionHighlight")}
                            descriptionText={t("header.descriptionText")}
                        />

                        {/* Tabs */}
                        <SensorTabs
                            tabs={tabs.map((tab: any) => ({
                                ...tab,
                                icon: iconMap[tab.id],
                            }))}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            isRTL={isRTL}
                        />

                        {/* Content */}
                        <motion.div
                            className="backdrop-blur-sm bg-white/60 rounded-3xl p-6 md:p-8 border border-gray-100 shadow-lg overflow-hidden relative"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            {/* Decorative gradients */}
                            <div className={`absolute -top-20 ${isRTL ? "left-20" : "right-20"} w-64 h-64 bg-gradient-to-br ${activeTabData.color} rounded-full opacity-10 blur-3xl`}></div>
                            <div className={`absolute -bottom-20 ${isRTL ? "right-20" : "left-20"} w-64 h-64 bg-gradient-to-tr from-blue-300 to-purple-300 rounded-full opacity-10 blur-3xl`}></div>

                            <div className="flex flex-col md:flex-row items-start gap-8 relative z-10">
                                {/* Sensor Content (Left/Right side) */}
                                <SensorContent
                                    activeTab={activeTab}
                                    tabIcon={iconMap[activeTabData.id]}
                                    tabColor={activeTabData.color}
                                    tabDescription={activeTabData.description}
                                    scenarios={(activeTabData.scenarios || []).map((scenario: any) => ({
                                        ...scenario,
                                        icon: scenarioIconMap[scenario.icon] || <User />, // Fallback to User icon
                                    }))}
                                    helpTitle={t("helpTitle")}
                                    isMobile={isMobile}
                                    isRTL={isRTL}
                                />

                                {/* Widget Display (Right/Left side) */}
                                <div className="w-full md:w-1/3 flex justify-center self-center">
                                    <AnimatePresence mode="wait">{widgetMap[activeTab]}</AnimatePresence>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default HealthSensors;
